import { parseCsvRows, normalizeAmount } from './readSheet';
import { headerToQuarter } from './columnMapping';
import { compareQuarters } from '../constants/quarterConfig';

/**
 * The quarterly Cost Worksheet (`Operations_Tracker_<quarter>(Cost_Worksheet).csv`)
 * → the generic `SKU,<quarter>…` sheet the import already understands.
 *
 * The worksheet carries every row twice: a raw-COST block and, further right
 * under the SAME quarter headers, a PRICE block (cost × (1 + the quarter's integration rate: 8 % to 2026-Q2, 12 % from 2026-Q3 per CONF-211), plus
 * labor on chassis). The catalog stores components (VCH-FRU-* drives,
 * CMP-SSD-*) as raw cost and products (nodes, NICs, VCH-NVME-*, JBODs) as
 * net price, so each row type must read from a different block. readSheet
 * takes one number per (ref, quarter) and cannot know which — hence this
 * transform, which is pure text-to-text and never touches the catalog.
 * Design: docs/superpowers/specs/2026-09-24-conf-209-q3-2026-price-import-design.md
 */

export interface IgnoredRow { ref: string; reason: string }
/** A row with no Vdura PN that the transform mapped to a catalog component
 *  anyway (`UNNUMBERED_COMPONENTS`); `label` is the sheet's own wording. */
export interface UnnumberedRow { sku: string; label: string }

export interface WorksheetSummary {
  fromQuarter: string;
  quarters: string[];      // canonical 'YYYY-Qn', sheet order, >= fromQuarter
  rows: number;            // output rows (a VCH-NVME row produces two)
  ignored: IgnoredRow[];   // each ignored part number seen, once
  skippedNoPn: number;     // data rows with no Vdura PN but at least one cost
  unnumbered: UnnumberedRow[];  // no-PN rows imported as catalog components, sheet order
}

/** Part numbers the worksheet will keep carrying that must NOT enter the
 *  catalog (CONF-209 decisions, 2026-09-24). Reported in the summary, never
 *  imported. Add a reason with every entry. */
export const IGNORED_PART_NUMBERS: ReadonlyMap<string, string> = new Map([
  ['VCH-5000-J108-1080', 'not offered through the configurator'],
  ['VCH-5000-J78-300', 'not offered through the configurator'],
  ['VCH-5000-J78-780', 'not offered through the configurator'],
  ['VCH-FRU-25GBE-2P-PCIE', 'node internal, not a catalog component'],
  ['VCH-FRU-SAS4-4P-PCIe', 'node internal, not a catalog component'],
  ['VCH-FRU-24Ts-J108-J78', 'discontinued; never alias to the 24T'],
  ['VCH-5000-D1N', 'retired (isActive: false); the catalog carries an unpriced terminator'],
]);

const HEADER_CELL = 'vdura pn';
const BARE_HEADER = 'bare';
const PRODUCT_HEADER = 'product';
const FRU_PREFIX = 'VCH-FRU-';
const NVME_PREFIX = 'VCH-NVME-';

/** `IGNORED_PART_NUMBERS`, keyed upper-case. `matchDraft.normalizeRef`
 *  lowercases for catalog matching, and the worksheet already drifts in case
 *  from the catalog (`vch-nvme-…`, `VCH-5000-d1n`), so an exact-case
 *  `Map.get` here would let a next-quarter re-spelling straight past the
 *  ignore list and into the catalog. Compare upper-case; keep the ORIGINAL
 *  `pn` (below) for anything that reaches the output or the summary, so
 *  today's sheet's spelling is unchanged. */
const IGNORED_BY_UPPER = new Map(
  [...IGNORED_PART_NUMBERS].map(([pn, reason]) => [pn.toUpperCase(), reason]),
);

/** Rows with no Vdura PN that nevertheless ARE catalog components, keyed
 *  `<Bare kind>|<size>` (the `Bare` column and the unnamed size column just
 *  before the second `Product` column), compared lower-case and trimmed.
 *
 *  CONF-209 skipped every no-PN row ("the catalog already matches") — true at
 *  2026-Q3 and false the next quarter: the Q4 sheet moved the 32G DIMM
 *  $1,200 → $2,500, the importer never saw it, so pass 2 solved every node
 *  base on the sheet against the stale DIMM and folded the whole rise into
 *  the base. A product sharing the DIMM but absent from the sheet (the Dual
 *  CPU node, 24 × 32G, costed from a separate table) kept the old DRAM and
 *  shipped ~$38K under-priced (CONF-264). Importing the DIMM makes pass 2
 *  solve the bases AGAINST it, and the absent product reprices through its
 *  composition.
 *
 *  Block: the DIMMs are stored bare → cost block. The JBOD chassis are stored
 *  uplifted + labor (`UPLIFT_EXEMPT_KINDS` carries 'chassis') → price block,
 *  and only when the row's Product is JBOD — the node chassis row is also
 *  `Chassis` but has no size and no catalog component. Node chassis, CPU and
 *  M.2 live inside each node's `CMP-BASE-*` remainder and stay skipped, as
 *  does any size not listed here: a new DIMM size is a catalog component
 *  first (seed + composition), then an entry here. */
const UNNUMBERED_COMPONENTS: ReadonlyMap<string, { sku: string; block: 'cost' | 'price'; product?: string }> =
  new Map([
    ['mem|64', { sku: 'CMP-DRAM-64G', block: 'cost' }],
    ['mem|32', { sku: 'CMP-DRAM-32G', block: 'cost' }],
    ['chassis|108', { sku: 'CMP-CHASSIS-J108', block: 'price', product: 'jbod' }],
    ['chassis|78', { sku: 'CMP-CHASSIS-J78', block: 'price', product: 'jbod' }],
  ]);

const norm = (cell: string | undefined): string => (cell ?? '').trim().toLowerCase();

/** Column indexes of the descriptive cells an unnumbered row is matched on:
 *  `Bare`, the `Product` column nearest before it, and the unnamed size
 *  column just before that. Null when the header lacks them — then no
 *  unnumbered row matches and every no-PN row is skipped, as before. */
function describeColumns(header: string[]): { bare: number; product: number; size: number } | null {
  const bare = header.findIndex((h) => norm(h) === BARE_HEADER);
  if (bare < 0) return null;
  let product = -1;
  for (let i = bare - 1; i >= 0; i -= 1) {
    if (norm(header[i]) === PRODUCT_HEADER) { product = i; break; }
  }
  if (product < 1) return null;
  return { bare, product, size: product - 1 };
}

/** True when the text carries the Cost Worksheet's two-block signature: a row
 *  whose first cell is `Vdura PN` (case-insensitive) on which at least one
 *  quarter header repeats (the cost block and, further right, the price
 *  block sharing the same quarter headers). A single-block sheet that merely
 *  spells its ref column `Vdura PN` — a valid alias for the generic prices
 *  path (columnMapping.ts) — must NOT trip this, or that button refuses a
 *  file the worksheet button then can't read either (no price column to
 *  find). The generic prices path refuses a true match and points at the
 *  worksheet button, instead of silently reading the sheet's junk first row
 *  as headers. */
export function isCostWorksheet(text: string): boolean {
  return parseCsvRows(text).some((r) => {
    if ((r[0] ?? '').toLowerCase() !== HEADER_CELL) return false;
    const seen = new Set<string>();
    for (const h of r) {
      const q = headerToQuarter(h);
      if (!q) continue;
      if (seen.has(q)) return true;
      seen.add(q);
    }
    return false;
  });
}

export function normalizeCostWorksheet(
  text: string, opts: { fromQuarter: string },
): { csv: string; summary: WorksheetSummary } {
  const rows = parseCsvRows(text);
  const headerIdx = rows.findIndex((r) => (r[0] ?? '').toLowerCase() === HEADER_CELL);
  if (headerIdx < 0) throw new Error('Not a cost worksheet: no "Vdura PN" header row');
  const header = rows[headerIdx]!;

  // First occurrence of a quarter header = cost column; second = price column.
  const cost = new Map<string, number>();
  const price = new Map<string, number>();
  header.forEach((h, i) => {
    const q = headerToQuarter(h);
    if (!q) return;
    if (!cost.has(q)) cost.set(q, i);
    else if (!price.has(q)) price.set(q, i);
  });
  const quarters = [...cost.keys()].filter((q) => compareQuarters(q, opts.fromQuarter) >= 0);
  if (quarters.length === 0) {
    throw new Error(`Cost worksheet has no quarter at or after ${opts.fromQuarter}`);
  }
  for (const q of quarters) {
    if (!price.has(q)) throw new Error(`Cost worksheet has no price column for ${q}`);
  }

  const amountAt = (row: string[], idx: number): string => {
    const raw = row[idx] ?? '';
    if (raw === '') return '';
    return normalizeAmount(raw) ?? '';     // '$-', '#N/A' → absent, never 0
  };

  const out: string[][] = [];
  const ignored: IgnoredRow[] = [];
  const unnumbered: UnnumberedRow[] = [];
  let skippedNoPn = 0;
  const emit = (sku: string, row: string[], block: Map<string, number>) => {
    out.push([sku, ...quarters.map((q) => amountAt(row, block.get(q)!))]);
  };
  const describe = describeColumns(header);
  const unnumberedOf = (row: string[]) => {
    if (!describe) return null;
    const hit = UNNUMBERED_COMPONENTS.get(`${norm(row[describe.bare])}|${norm(row[describe.size])}`);
    if (!hit || (hit.product !== undefined && norm(row[describe.product]) !== hit.product)) return null;
    const label = `${(row[describe.bare] ?? '').trim()} ${(row[describe.size] ?? '').trim()} ` +
                  `(${(row[describe.product] ?? '').trim()})`;
    return { ...hit, label };
  };

  for (const row of rows.slice(headerIdx + 1)) {
    const pn = row[0] ?? '';
    if (pn === '') {
      const hit = unnumberedOf(row);
      if (hit) {
        emit(hit.sku, row, hit.block === 'cost' ? cost : price);
        unnumbered.push({ sku: hit.sku, label: hit.label });
        continue;
      }
      if (quarters.some((q) => amountAt(row, cost.get(q)!) !== '')) skippedNoPn += 1;
      continue;
    }
    const pnUpper = pn.toUpperCase();
    const reason = IGNORED_BY_UPPER.get(pnUpper);
    if (reason !== undefined) {
      if (!ignored.some((i) => i.ref === pn)) ignored.push({ ref: pn, reason });
      continue;
    }
    if (pnUpper.startsWith(FRU_PREFIX)) {
      emit(pn, row, cost);
    } else if (pnUpper.startsWith(NVME_PREFIX)) {
      emit(`CMP-SSD-${pn.slice(NVME_PREFIX.length)}`, row, cost);
      emit(pn, row, price);
    } else {
      emit(pn, row, price);
    }
  }

  const csv = [['SKU', ...quarters], ...out].map((r) => r.join(',')).join('\n') + '\n';
  return {
    csv,
    summary: { fromQuarter: opts.fromQuarter, quarters, rows: out.length, ignored, skippedNoPn, unnumbered },
  };
}
